import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/shared/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/ui/table';
import { ToggleGroup, ToggleGroupItem } from '@/shared/ui/toggle-group';
import { cn } from '@/shared/utils/date-utils';
import type { OrganizationOverviewData } from '@/shared/types/overviewTypes';
import {
  Bar,
  YAxis,
  Tooltip as RechartsTooltip,
  Legend,
  LabelList,
  XAxis,
} from 'recharts';
import {
  BAR_TOP_RADIUS,
  CHART_OPACITY,
  calendarPrevYm,
  monthKeyFromStartDate,
  TICK_FONT_SIZE,
  LABEL_FONT_SIZE,
} from '@/domains/global-dashboard/utils/chart-utils';
import { formatNumber } from '@/shared/utils/format-utils';
import {
  DashboardBarChart,
  DashboardBarGrid,
  DashboardBarPlotHoverCursor,
} from '@/domains/global-dashboard/components/GlobalDashboardBarChart';
import { barTopNumericLabelProps } from '@/domains/global-dashboard/components/dashboardChart';
import { useMemo, useState } from 'react';
import { ServiceLineStatus } from '@/shared/types/serviceLineTypes';
import { DashboardSummarySection, SummaryCard } from '@/domains/global-dashboard/sections/DashboardSummarySection';
import { SectionType } from '@/domains/global-dashboard/utils/chart-utils';
import { getServiceLineStatusChartFill, getServiceLineStatusTextColorClass } from '@/shared/utils/color-utils-service-lines';
import { getActiveRateColorClass } from '@/shared/utils/color-utils-data-usages';
import { FleetEvolutionChart } from '@/domains/global-dashboard/sections/service-lines/FleetEvolutionChart';
import { fleetSnapshotByYm } from '@/domains/global-dashboard/sections/service-lines/fleet-utils';

/** Main component for the fleet overview section. */
export function FleetOverviewSection({
  selectedOrganizations,
  accountChartColorById,
  onNavigateToOrganization,
}: {
  selectedOrganizations: OrganizationOverviewData[];
  accountChartColorById: Map<string, string>;
  onNavigateToOrganization: (organizationId: string, sectionType: SectionType) => void;
}) {
  const [fleetSurface, setFleetSurface] = useState<'chart' | 'table'>('chart');
  const [fleetBarMetric, setFleetBarMetric] = useState<'total' | 'active'>('total');

  const organizationSeries = useMemo(() => {
    return selectedOrganizations.map((organization) => ({
      organization,
      months: fleetSnapshotByYm(organization),
    }));
  }, [selectedOrganizations]);

  const breakdownRows = useMemo(() => {
    const reportingYm = monthKeyFromStartDate(new Date().toISOString());
    const rows = organizationSeries
      .map(({ organization, months }) => {
        const curSnap = months.get(reportingYm);
        if (!curSnap) return null;

        const prevYm = calendarPrevYm(reportingYm);
        const prevSnap = months.get(prevYm);

        const total = curSnap.total;
        const prevTotal = prevSnap !== undefined ? prevSnap.total : null;
        const netNewMonth = prevTotal !== null ? total - prevTotal : 0;
        const momPct = prevTotal !== null && prevTotal > 0 ? ((total - prevTotal) / prevTotal) * 100 : null;

        const active = curSnap.active;
        const exhausted = curSnap.exhausted;
        const offline = curSnap.offline;
        const noTerminal = curSnap.noTerminal;
        const suspended = curSnap.suspended;
        const others = offline + noTerminal + suspended;
        const ar = total > 0 ? (active / total) * 100 : 0;

        return {
          row: organization,
          total,
          active,
          exhausted,
          offline,
          noTerminal,
          suspended,
          others,
          activeRate: formatNumber(ar),
          netNewMonth,
          momPct,
        };
      })
      .filter((r) => r !== null)
      .sort((a, b) => b.total - a.total);

    return rows;
  }, [organizationSeries]);

  const summaryCards: SummaryCard[] = useMemo(() => {
    const netChange = breakdownRows.reduce((s, r) => s + (r.netNewMonth ?? 0), 0);
    const activeSLs = breakdownRows.reduce((s, r) => s + r.active, 0);
    const exhaustedSLs = breakdownRows.reduce((s, r) => s + r.exhausted, 0);
    const offlineSLs = breakdownRows.reduce((s, r) => s + r.offline, 0);
    const noTerminalSLs = breakdownRows.reduce((s, r) => s + r.noTerminal, 0);
    const suspendedSLs = breakdownRows.reduce((s, r) => s + r.suspended, 0);
    const totalSLs = breakdownRows.reduce((s, r) => s + r.total, 0);
    const deployedSLs = totalSLs - suspendedSLs;
    const activeRate = totalSLs > 0 ? (activeSLs / totalSLs) * 100 : 0;
    return [
      { title: 'Deployed', currentValue: `${deployedSLs}`, currentValueTextColor: getServiceLineStatusTextColorClass(ServiceLineStatus.ACTIVE) },
      { title: 'Active', currentValue: `${activeSLs}`, currentValueTextColor: getServiceLineStatusTextColorClass(ServiceLineStatus.ACTIVE) },
      { title: 'Exhausted', currentValue: `${exhaustedSLs}`, currentValueTextColor: getServiceLineStatusTextColorClass(ServiceLineStatus.EXHAUSTED) },
      { title: 'Standby (No Terminal)', currentValue: `${noTerminalSLs}`, currentValueTextColor: getServiceLineStatusTextColorClass(ServiceLineStatus.NO_UT) },
      { title: 'Offline', currentValue: `${offlineSLs}`, currentValueTextColor: getServiceLineStatusTextColorClass(ServiceLineStatus.OFFLINE) },
      { title: 'Suspended', currentValue: `${suspendedSLs}`, currentValueTextColor: getServiceLineStatusTextColorClass(ServiceLineStatus.SUSPENDED) },
      { title: 'Active Rate', currentValue: `${formatNumber(activeRate)}%`, currentValueTextColor: getActiveRateColorClass(activeRate) },
      {
        title: 'Net Change vs Previous Month',
        currentValue: `${netChange}`,
        currentValueTextColor: netChange >= 0 ? 'text-green-500' : 'text-red-500',
      },
    ];
  }, [breakdownRows]);

  return (
    <section className="space-y-4 border rounded-md p-4 bg-muted/50">
      <div className="flex items-start gap-2 justify-between">
        <div>
          <h2 className="text-lg font-semibold">Service Lines Overview</h2>
          <p className="text-sm">Executive snapshot and service lines breakdown</p>
        </div>
        <div className="flex-col text-sm flex items-end gap-2">
          <span className="font-medium">Active rate tiers:</span>
          <div className="flex flex-wrap items-center gap-2 font-medium">
            <span className={cn(getActiveRateColorClass(80), 'font-bold')}>&gt;80%</span>
            <span>•</span>
            <span className={cn(getActiveRateColorClass(60), 'font-bold')}>&gt;60% and &lt;=80%</span>
            <span>•</span>
            <span className={cn(getActiveRateColorClass(40), 'font-bold')}>&lt;=60%</span>
          </div>
        </div>
      </div>

      {/* Service lines summary (current month) */}
      <DashboardSummarySection
        section={{
          type: SectionType.FLEET_OVERVIEW,
          title: 'Service lines summary (current month)',
          description: 'Aggregated month-to-date service lines summary. Deployed service lines do not include suspended service lines.'
        }}
        summaryCards={summaryCards}
      />

      {/* Service lines breakdown (current month) */}
      <Card>
        <CardHeader className="p-4 space-y-4">
          <div className="grid grid-cols-2 gap-4 justify-end items-center w-full">
            <div className="col-span-1">
              <CardTitle className="text-base">Service lines breakdown (current month)</CardTitle>
              <CardDescription>
                Total view shows Active, Exhausted, and offline or non-operational counts (offline, no terminal, suspended). Active view shows Active service lines only.
              </CardDescription>
            </div>
            <div className="col-span-1 flex items-center justify-end">
              <ToggleGroup
                className="justify-end"
                type="single"
                value={fleetSurface}
                onValueChange={(v) => v && setFleetSurface(v as 'chart' | 'table')}
                size="sm"
                variant="outline"
              >
                <ToggleGroupItem value="chart">Chart</ToggleGroupItem>
                <ToggleGroupItem value="table">Table</ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>
          {fleetSurface === 'chart' && (
            <div className="flex flex-col gap-2 justify-end">
              <ToggleGroup
                type="single"
                value={fleetBarMetric}
                onValueChange={(v) => v && setFleetBarMetric(v as 'total' | 'active')}
                size="sm"
                variant="outline"
              >
                <ToggleGroupItem value="total">All</ToggleGroupItem>
                <ToggleGroupItem value="active">Active</ToggleGroupItem>
              </ToggleGroup>
            </div>
          )}
        </CardHeader>
        <CardContent className="p-4 pt-0 overflow-x-auto">
          {fleetSurface === 'table' ? (
            <div className="overflow-x-auto rounded-lg border">
              <Table className="w-full border-collapse">
                <TableHeader>
                  <TableRow className="bg-muted font-medium">
                    <TableHead>Market</TableHead>
                    <TableHead className="text-right">Total</TableHead>
                    <TableHead className="text-right">Active</TableHead>
                    <TableHead className="text-right">Exhausted</TableHead>
                    <TableHead className="text-right">Offline</TableHead>
                    <TableHead className="text-right">Standby (No Terminal)</TableHead>
                    <TableHead className="text-right">Suspended</TableHead>
                    <TableHead className="text-right">Active Rate</TableHead>
                    <TableHead className="text-right">Net Change vs Previous Month</TableHead>
                    <TableHead className="text-right">MoM Growth</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {breakdownRows.map(
                    ({ row, total, active, exhausted, offline, noTerminal, suspended, netNewMonth, momPct, activeRate }) => (
                      <TableRow
                        key={row.organizationId}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => onNavigateToOrganization(row.organizationId, SectionType.FLEET_OVERVIEW)}
                      >
                        <TableCell className="font-medium">{row.name}</TableCell>
                        <TableCell className="text-right">{total}</TableCell>
                        <TableCell className="text-right">{active}</TableCell>
                        <TableCell className="text-right">{exhausted}</TableCell>
                        <TableCell className="text-right">{offline}</TableCell>
                        <TableCell className="text-right">{noTerminal}</TableCell>
                        <TableCell className="text-right">{suspended}</TableCell>
                        <TableCell className={cn('text-right', getActiveRateColorClass(activeRate))}>{activeRate}%</TableCell>
                        <TableCell className="text-right">{netNewMonth > 0 ? `+ ${netNewMonth} ` : netNewMonth}</TableCell>
                        <TableCell className="text-right">
                          {momPct == null ? '—' : `${momPct >= 0 ? '+' : ''}${formatNumber(momPct)}% `}
                        </TableCell>
                      </TableRow>
                    )
                  )}
                </TableBody>
              </Table>
            </div>
          ) : breakdownRows.length === 0 ? (
            <p className="text-sm py-8 text-center">No service line data for the current month.</p>
          ) : (
            <DashboardBarChart data={breakdownRows} variant="perMarket">
              <DashboardBarGrid />
              <XAxis
                dataKey="row.name"
                tick={{ fontSize: TICK_FONT_SIZE }}
                interval={0}
                textAnchor="middle"
              />
              <YAxis
                tick={{ fontSize: TICK_FONT_SIZE }}
                allowDecimals={false}
                label={{ value: 'Number of Service Lines', angle: -90, position: 'middle', fontSize: LABEL_FONT_SIZE }}
              />
              <RechartsTooltip formatter={(value: number) => formatNumber(value)} cursor={<DashboardBarPlotHoverCursor />} />
              {fleetBarMetric === 'total' ? (
                <>
                  <Legend />
                  <Bar dataKey="active" stackId="fleet" name="Active" fill={getServiceLineStatusChartFill(ServiceLineStatus.ACTIVE)} fillOpacity={CHART_OPACITY.fleetStack} />
                  <Bar dataKey="exhausted" stackId="fleet" name="Exhausted" fill={getServiceLineStatusChartFill(ServiceLineStatus.EXHAUSTED)} fillOpacity={CHART_OPACITY.fleetStack} />
                  <Bar dataKey="others" stackId="fleet" name="Offline and Others" fill={getServiceLineStatusChartFill(ServiceLineStatus.OFFLINE)} fillOpacity={CHART_OPACITY.fleetStack} radius={BAR_TOP_RADIUS}>
                    <LabelList
                      {...barTopNumericLabelProps}
                      formatter={(value: unknown) => formatNumber(typeof value === 'number' ? value : Number(value))}
                    />
                  </Bar>
                </>
              ) : (
                <>
                  <Legend />
                  <Bar dataKey="active" name="Active" fill={getServiceLineStatusChartFill(ServiceLineStatus.ACTIVE)} fillOpacity={CHART_OPACITY.fleetStack} radius={BAR_TOP_RADIUS}>
                    <LabelList
                      {...barTopNumericLabelProps}
                      formatter={(value: unknown) => formatNumber(typeof value === 'number' ? value : Number(value))}
                    />
                  </Bar>
                </>
              )}
            </DashboardBarChart>
          )}
        </CardContent>
      </Card>

      {/* Service lines evolution */}
      <FleetEvolutionChart
        selectedOrganizations={selectedOrganizations}
        accountChartColorById={accountChartColorById}
      />
    </section>
  );
}
